import { useMemo, useState } from "react";
import { Search, UserRoundCheck } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";

type ArtistProfile = {
  id: Id<"artists">;
  name: string;
  stageName?: string;
};

type Props = {
  email: string;
  logout: () => void | Promise<void>;
  profiles: ArtistProfile[] | undefined;
};

export default function ArtistProfileLinker({
  email,
  logout,
  profiles,
}: Props) {
  const linkArtistProfile = useMutation(api.mutations.linkArtistProfile);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");

  const filteredProfiles = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!profiles || !value) return profiles ?? [];
    return profiles.filter((profile) =>
      `${profile.name} ${profile.stageName ?? ""}`.toLowerCase().includes(value),
    );
  }, [profiles, query]);

  const selectedProfile = profiles?.find(
    (profile) => profile.id === selectedId,
  );

  async function confirmLink() {
    if (!selectedProfile || linking) return;
    setError("");
    setLinking(true);
    try {
      await linkArtistProfile({ artistId: selectedProfile.id });
    } catch (linkError) {
      setError(
        linkError instanceof Error
          ? linkError.message
          : "Could not link this artist profile.",
      );
      setLinking(false);
    }
  }

  return (
    <main className="profile-link-page">
      <section className="profile-link-card">
        <div className="profile-link-icon" aria-hidden="true">
          <UserRoundCheck size={26} />
        </div>
        <p className="profile-link-label">Set up your artist portal</p>
        <h1>Find your artist profile</h1>
        <p className="profile-link-intro">
          Search for your artist name, select it, then confirm the link to{" "}
          <strong>{email}</strong>.
        </p>
        <div className="profile-link-warning">
          Choose carefully. Each account and artist profile can only be linked
          once.
        </div>

        <label className="profile-link-search">
          <Search size={18} aria-hidden="true" />
          <input
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedId(null);
              setError("");
            }}
            placeholder="Search artist name"
            type="search"
            value={query}
          />
        </label>

        <div className="profile-link-results" aria-live="polite">
          {profiles === undefined ? (
            <p className="profile-link-empty">Loading artist profiles…</p>
          ) : filteredProfiles.length === 0 ? (
            <p className="profile-link-empty">
              {query
                ? "No unclaimed artist profile matches that search."
                : "No unclaimed artist profiles are available."}
            </p>
          ) : (
            filteredProfiles.map((profile) => (
              <button
                className={profile.id === selectedId ? "selected" : ""}
                key={profile.id}
                onClick={() => {
                  setSelectedId(profile.id);
                  setError("");
                }}
                type="button"
              >
                <span>{profile.name}</span>
                {profile.stageName && profile.stageName !== profile.name ? (
                  <small>{profile.stageName}</small>
                ) : null}
              </button>
            ))
          )}
        </div>

        {selectedProfile ? (
          <div className="profile-link-confirm">
            <p>
              Link this account to <strong>{selectedProfile.name}</strong>?
            </p>
            <button disabled={linking} onClick={confirmLink} type="button">
              {linking ? "Linking…" : "Confirm and open portal"}
            </button>
          </div>
        ) : null}

        {error ? <p className="profile-link-error">{error}</p> : null}

        <button className="profile-link-signout" onClick={logout} type="button">
          Sign out and use another account
        </button>
      </section>
    </main>
  );
}
